import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import {
  type ApiOutputs,
  type TemplateProposalBody,
  templateProposalInputSchema,
  templateProposalSchema,
} from "@rosc/contracts";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { referenceClient } from "@/lib/orpc/client";
import {
  getVisitScope,
  useVisitScope,
  type VisitScope,
} from "@/lib/visit-queries";

const orpc = createTanstackQueryUtils(referenceClient);
type Lifetime = { scope: VisitScope | null; source: string };
type State = {
  lifetime: Lifetime;
  phase: "pending" | "success" | "error";
  result?: ApiOutputs["createTemplateProposal"];
};
export function useTemplateProposal(body: TemplateProposalBody) {
  const scope = useVisitScope();
  const source = JSON.stringify(body);
  const active = useRef<Lifetime>({ scope, source });
  if (active.current.scope !== scope || active.current.source !== source)
    active.current = { scope, source };
  const lifetime = active.current;
  const mounted = useRef(true),
    pending = useRef<Lifetime | null>(null);
  const [state, setState] = useState<State | null>(null);
  const mutation = useMutation(
    orpc.createTemplateProposal.mutationOptions({
      context: { expectedUserUid: scope?.uid },
      retry: false,
      gcTime: 0,
    }),
  );
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const current = (): boolean =>
    mounted.current &&
    active.current === lifetime &&
    !!scope &&
    getVisitScope() === scope;
  const propose = async (): Promise<void> => {
    if (!current() || pending.current === lifetime) return;
    pending.current = lifetime;
    setState({ lifetime, phase: "pending" });
    try {
      const input = templateProposalInputSchema.parse(body);
      const result = templateProposalSchema.parse(
        await mutation.mutateAsync({ body: input, query: {} }),
      );
      if (result.templatePublicId !== (input.templatePublicId ?? null))
        throw Error("Unexpected template identity");
      if (current()) setState({ lifetime, phase: "success", result });
    } catch {
      if (current()) setState({ lifetime, phase: "error" });
    } finally {
      if (pending.current === lifetime) {
        pending.current = null;
        mutation.reset();
      }
    }
  };
  return {
    propose,
    result: state?.lifetime === lifetime ? (state.result ?? null) : null,
    proposing: state?.lifetime === lifetime && state.phase === "pending",
    failed: state?.lifetime === lifetime && state.phase === "error",
    clear: (): void => setState(null),
    isCurrent: current,
  };
}
